import { Extension } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { EditorState } from "@tiptap/pm/state";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import {
  isTemplateActive,
  keysConditionState,
  subscribeConditionRefresh,
} from "./conditional-evaluator";
import { CONDITIONAL_NODE_NAME, CONDITIONAL_TEMPLATE_ATTR } from "./constants";
import { collectReferencedKeys } from "./resolution";

/**
 * Conditional table *cells*. Unlike a conditional table *row* (which gates whether a
 * whole row appears — see `conditional-table-row.ts`), a conditional placed inside a
 * single cell gates only that cell's *value*; the cell itself is never hidden. The
 * conditional lives in the cell as an ordinary inline `conditional` node (inserted
 * via the `/` menu, round-tripped through markdown by the backend), so no schema
 * change is needed here.
 *
 * This extension only adds the *visual* treatment, mirroring conditional rows: in
 * the editable editor a cell containing a conditional is ringed in the conditional
 * colour (green/yellow/red by resolution) and dimmed while its active branch is
 * currently empty. There is no read-only treatment: the cell always stays, and the
 * conditional node itself renders the resolved value (or nothing) in the preview.
 */

const CELL_TYPES = new Set(["tableCell", "tableHeader"]);

const conditionalCellPluginKey = new PluginKey("conditionalTableCells");

/** The conditional `template` of the first conditional node inside a cell, if any. */
const cellConditionalTemplate = (cell: ProseMirrorNode): string | null => {
  let template: string | null = null;
  cell.descendants((child) => {
    if (template !== null) {
      return false;
    }
    if (child.type.name === CONDITIONAL_NODE_NAME) {
      template = (child.attrs[CONDITIONAL_TEMPLATE_ATTR] as string) ?? "";
      return false;
    }
    return true;
  });
  return template;
};

const buildDecorations = (
  doc: ProseMirrorNode,
  ringCells: boolean
): DecorationSet => {
  // A conditional cell is never hidden — only its value changes — so the read-only
  // preview needs no decoration (the conditional node renders the value itself).
  if (!ringCells) {
    return DecorationSet.empty;
  }
  const decorations: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (!CELL_TYPES.has(node.type.name)) {
      return;
    }
    const template = cellConditionalTemplate(node);
    if (template === null) {
      // No conditional in this cell; keep descending in case nested tables exist.
      return;
    }
    const classes = ["formly-conditional-cell"];
    // Ring colour tracks resolution (green/yellow/red), like the chip and rows.
    const state = keysConditionState(collectReferencedKeys(template));
    if (state !== "empty") {
      classes.push(`formly-conditional-cell--${state}`);
    }
    // Dim the cell while the active branch is currently empty (value won't show).
    if (!isTemplateActive(template)) {
      classes.push("formly-conditional-cell--inactive");
    }
    decorations.push(
      Decoration.node(pos, pos + node.nodeSize, { class: classes.join(" ") })
    );
    // We found this cell's conditional; no need to walk its children again.
    return false;
  });
  return DecorationSet.create(doc, decorations);
};

export interface ConditionalTableCellOptions {
  /**
   * Also ring/dim conditional cells when the editor is read-only. Used by the
   * frames template preview, which is read-only but should look like the
   * authoring view.
   */
  decorateReadOnly: boolean;
}

export const ConditionalTableCell =
  Extension.create<ConditionalTableCellOptions>({
    name: "conditionalTableCell",

    addOptions() {
      return {
        decorateReadOnly: false,
      };
    },

    addProseMirrorPlugins() {
      const editor = this.editor;
      const { decorateReadOnly } = this.options;
      const ringCells = () => editor.isEditable || decorateReadOnly;
      return [
        new Plugin({
          key: conditionalCellPluginKey,
          state: {
            init: (_, state: EditorState) =>
              buildDecorations(state.doc, ringCells()),
            apply(tr, old, _oldState, newState) {
              if (tr.docChanged || tr.getMeta(conditionalCellPluginKey)) {
                return buildDecorations(newState.doc, ringCells());
              }
              return old.map(tr.mapping, tr.doc);
            },
          },
          props: {
            decorations(state) {
              return conditionalCellPluginKey.getState(state);
            },
          },
          view: (view) => {
            // Recompute decorations when resolved data lands (out-of-band of any
            // document change), via the provider's `setConditionEvaluator`.
            const unsubscribe = subscribeConditionRefresh((): void => {
              try {
                view.dispatch(
                  view.state.tr.setMeta(conditionalCellPluginKey, true)
                );
              } catch {
                // The view may be tearing down; ignore.
              }
            });
            return {
              destroy() {
                unsubscribe();
              },
            };
          },
        }),
      ];
    },
  });

export default ConditionalTableCell;
